<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 700));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "linear-gradient(120deg, #fb7185, #f97316 30%, #facc15 60%, #34d399 100%)",
      fontFamily: props.block.fontFamily || "'Inter', system-ui, sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      backgroundSize: "300% 300%",
      animation: "cd-liquid-bg 14s ease infinite",
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.5}px`,
    height: `${base * 2.5}px`,
    fontSize: `${base}px`,
  };
});
</script>

<template>
  <div class="cd-liquid" :style="containerStyle">
    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-liquid__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        animationDelay: `${i * 800}ms, ${i * 120}ms`,
      }"
    >
      <span class="cd-liquid__inner">
        <span
          class="cd-liquid__digit"
          :key="seg.value"
          :style="{
            fontSize: cardSize.fontSize,
            color: block.digitColor || '#ffffff',
          }"
        >
          {{ seg.value }}
        </span>
        <span
          class="cd-liquid__label"
          :style="{
            fontSize: `${block.labelFontSize}px`,
            color: block.labelColor || 'rgba(255,255,255,0.92)',
          }"
        >
          {{ seg.label }}
        </span>
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-liquid {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px;
  overflow: hidden;
}
@keyframes cd-liquid-bg {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}
.cd-liquid__cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  animation:
    cd-liquid-blob 9s ease-in-out infinite,
    cd-liquid-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
  will-change: border-radius, transform;
}
@keyframes cd-liquid-blob {
  0%,
  100% {
    border-radius: 38% 62% 70% 30% / 50% 35% 65% 50%;
    transform: rotate(0deg);
  }
  33% {
    border-radius: 60% 40% 30% 70% / 60% 60% 40% 40%;
    transform: rotate(6deg);
  }
  66% {
    border-radius: 30% 70% 50% 50% / 40% 50% 60% 60%;
    transform: rotate(-4deg);
  }
}
@keyframes cd-liquid-rise {
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
  }
}
.cd-liquid__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.cd-liquid__digit {
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  display: inline-block;
  animation: cd-liquid-morph var(--cd-tick, 700ms) cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cd-liquid-morph {
  0% {
    opacity: 0;
    transform: scale(0.5) rotate(-10deg);
    filter: blur(6px);
  }
  60% {
    opacity: 1;
    transform: scale(1.1) rotate(2deg);
    filter: blur(0);
  }
  100% {
    transform: scale(1) rotate(0);
  }
}
.cd-liquid__label {
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  font-weight: 600;
}
</style>
